/**
 * MailboxSnoozeDialog — pick a wake time for a message (external-client rebuild;
 * distinct from the legacy SnoozeDialog). Presets + a custom datetime-local.
 * Snoozing hides the message from the inbox until that time (v1 local overlay);
 * it resurfaces automatically once the time passes.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useSnoozeMessage } from '../hooks/useMailbox';

interface MailboxSnoozeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: string | null;
  onSnoozed?: () => void;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** datetime-local wants `YYYY-MM-DDTHH:mm` in local time. */
function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

function tomorrowMorning(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}

function nextWeek(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setHours(9, 0, 0, 0);
  return d;
}

export function MailboxSnoozeDialog({
  open,
  onOpenChange,
  messageId,
  onSnoozed,
}: MailboxSnoozeDialogProps) {
  const snooze = useSnoozeMessage();
  const [at, setAt] = useState('');

  useEffect(() => {
    if (!open) return;
    setAt(toLocalInput(tomorrowMorning()));
    snooze.reset();
    // Re-seed defaults only when the dialog opens.
  }, [open]);

  const date = at ? new Date(at) : null;
  const valid = !!date && !Number.isNaN(date.getTime()) && date.getTime() > Date.now();
  const canSnooze = !!messageId && valid && !snooze.isPending;

  const handleSnooze = () => {
    if (!messageId || !date) return;
    snooze.mutate(
      { messageId, snoozeUntil: date.toISOString() },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSnoozed?.();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Snooze until</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAt(toLocalInput(tomorrowMorning()))}
            >
              Tomorrow
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAt(toLocalInput(nextWeek()))}
            >
              Next week
            </Button>
          </div>
          <div className="space-y-1">
            <Label htmlFor="snooze-at">Wake at</Label>
            <Input
              id="snooze-at"
              type="datetime-local"
              value={at}
              onChange={(e) => setAt(e.target.value)}
            />
            {at && !valid && <p className="text-xs text-destructive">Pick a time in the future.</p>}
          </div>
          {snooze.isError && (
            <p className="text-sm text-destructive">{(snooze.error as Error)?.message}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={snooze.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSnooze} disabled={!canSnooze}>
            {snooze.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Snooze'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
